import "@fontsource-variable/archivo";
import "@fontsource-variable/recursive";
import "@web-git-graph/web/register";
import { GitHubGitGraphProvider } from "@web-git-graph/web/providers/github";
import { HttpGitGraphProvider } from "@web-git-graph/web/providers/http";
import { GitGraphProtocolError } from "@web-git-graph/protocol";
import type { WebGitGraphElement } from "@web-git-graph/web";
import { initPage } from "./page";
import "./style.css";

/**
 * Fullscreen stage: the graph is the page. Everything the reader needs to see a
 * repository's history is in the URL (`?repo=owner/name&ref=main`), so the page
 * can be linked to from a README, a chat message or an issue and open straight
 * into that repository.
 */

interface Target {
  repository: string;
  refs: readonly string[];
}

type StatusState =
  | { kind: "empty" }
  | { kind: "connecting"; repository: string }
  | { kind: "connected"; repository: string }
  | { kind: "backend"; backend: string; repository: string }
  | { kind: "rateLimited" }
  | { kind: "error"; message: string };

/** Shared with the landing page, so a token pasted there is still in effect here. */
const TOKEN_KEY = "web-git-graph.demo.github-token";

const messages = {
  en: {
    "theme.toDark": "Switch to dark theme",
    "theme.toLight": "Switch to light theme",
    "stage.repoLabel": "GitHub repository",
    "stage.repoPlaceholder": "owner/name or https://github.com/owner/name",
    "stage.load": "Load",
    "stage.share": "Share",
    "stage.tokenLabel": "Token",
    "stage.tokenPlaceholder": "Paste a GitHub token if you hit the rate limit",
    "stage.statusEmpty":
      "Enter a public GitHub repository, or use ?repo=owner/name in the address bar.",
    "stage.emptyTitle": "Any public repository, fullscreen.",
    "stage.emptyBody":
      "Type owner/name above, or put the repository straight into the address bar — the link opens on this graph.",
    "stage.examples": "Try",
    "stage.statusConnecting": "Loading github.com/{repository}…",
    "stage.statusConnected":
      "{repository} · Ctrl/Cmd-click a second commit to compare, double-click to open it on GitHub.",
    "stage.statusBackend": "Connected to {backend} · repository {repository}",
    "stage.statusError": "GitHub API: {message}",
    "stage.statusRateLimited":
      "Rate limit reached (the unauthenticated GitHub API allows 60 requests/hour per IP). Paste a personal access token on the right — a classic token with public_repo is enough, and it stays in this tab only.",
    "share.title": "Share this graph",
    "share.body":
      "Send the link, or drop the badge into your repository README so readers reach this graph in one click.",
    "share.url": "Fullscreen link",
    "share.markdown": "README badge (Markdown)",
    "share.html": "HTML anchor",
    "share.preview": "Badge preview",
    "share.close": "Close",
    "common.copy": "Copy",
    "common.copied": "Copied"
  },
  zh: {
    "theme.toDark": "切换到深色主题",
    "theme.toLight": "切换到浅色主题",
    "stage.repoLabel": "GitHub 仓库",
    "stage.repoPlaceholder": "owner/name 或 https://github.com/owner/name",
    "stage.load": "载入",
    "stage.share": "分享",
    "stage.tokenLabel": "Token",
    "stage.tokenPlaceholder": "触发限流时粘贴 GitHub token",
    "stage.statusEmpty": "输入一个公开 GitHub 仓库，或在地址栏使用 ?repo=owner/name。",
    "stage.emptyTitle": "全屏查看任意公开仓库的提交历史",
    "stage.emptyBody": "在上方输入 owner/name，或者把仓库直接写进地址栏——这个链接打开就是这张图。",
    "stage.examples": "试试",
    "stage.statusConnecting": "正在载入 github.com/{repository}…",
    "stage.statusConnected":
      "{repository} · 按住 Ctrl/Cmd 点击第二个提交可比较，双击可在 GitHub 打开。",
    "stage.statusBackend": "已连接 {backend} · 仓库 {repository}",
    "stage.statusError": "GitHub API：{message}",
    "stage.statusRateLimited":
      "已触发速率限制（未认证的 GitHub API 每个 IP 每小时 60 次）。请在右侧粘贴 personal access token——classic token 勾选 public_repo 即可，且只保存在本标签页。",
    "share.title": "分享这张图",
    "share.body": "把链接发出去，或者把徽章放进仓库 README，读者点一下就能看到这张图。",
    "share.url": "全屏链接",
    "share.markdown": "README 徽章（Markdown）",
    "share.html": "HTML 链接标签",
    "share.preview": "徽章预览",
    "share.close": "关闭",
    "common.copy": "Copy",
    "common.copied": "Copied"
  }
};

const graph = document.querySelector<WebGitGraphElement>("#graph")!;
const form = document.querySelector<HTMLFormElement>("#repo-form")!;
const repositoryInput = document.querySelector<HTMLInputElement>("#repository")!;
const tokenInput = document.querySelector<HTMLInputElement>("#github-token")!;
const status = document.querySelector<HTMLElement>("#status")!;
const statusLine = document.querySelector<HTMLElement>("#status-line")!;
const sourceLink = document.querySelector<HTMLAnchorElement>("#source-link")!;
const shareSheet = document.querySelector<HTMLElement>("#share-sheet")!;
const shareToggle = document.querySelector<HTMLButtonElement>("#share-toggle")!;
const shareClose = document.querySelector<HTMLButtonElement>("#share-close")!;

let statusState: StatusState = { kind: "empty" };
let target: Target | undefined;

const page = initPage({
  messages,
  documentTitle: {
    en: "Commit graph — Web Git Graph",
    zh: "提交历史图 — Web Git Graph"
  },
  onLocale: () => {
    renderStatus();
    renderTitle();
  },
  onTheme: (theme) => {
    graph.theme = theme;
  }
});

graph.theme = page.theme;
tokenInput.value = sessionStorage.getItem(TOKEN_KEY) ?? "";

/**
 * Accepts anything a reader is likely to paste: `owner/name`, a clone URL, or a
 * deep link such as `https://github.com/owner/name/tree/release/2.x` — in which
 * case the branch after `/tree/` becomes the ref the graph is walked from.
 */
function parseTarget(raw: string): Target | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const withoutHost = trimmed
    .replace(/^git@github\.com:/i, "")
    .replace(/^(?:https?:\/\/)?(?:www\.)?github\.com\//i, "")
    .replace(/[?#].*$/, "")
    .replace(/\.git$/i, "")
    .replace(/^\/+|\/+$/g, "");
  const [owner, name, kind, ...rest] = withoutHost.split("/");
  if (!owner || !name) return undefined;
  // Branch names contain slashes, so everything after /tree/ is one ref.
  const ref = (kind === "tree" || kind === "commits") && rest.length ? rest.join("/") : "";
  return { repository: `${owner}/${name}`, refs: ref ? [ref] : [] };
}

function readTargetFromLocation(): Target | undefined {
  const query = new URLSearchParams(window.location.search);
  const parsed = parseTarget(query.get("repo") ?? query.get("r") ?? "");
  if (!parsed) return undefined;
  const refs = (query.get("ref") ?? query.get("refs") ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  return refs.length ? { ...parsed, refs } : parsed;
}

/**
 * Slashes are legal in a query string and `owner/name` is the whole point of
 * the link, so they are kept literal — `URLSearchParams` would percent-encode
 * them into something nobody wants to paste into a README.
 */
function queryValue(value: string): string {
  return encodeURIComponent(value).replace(/%2F/g, "/");
}

function stageHref(): string {
  return new URL(window.location.pathname, window.location.origin).href;
}

function shareUrl(value: Target): string {
  const ref = value.refs.length ? `&ref=${queryValue(value.refs.join(","))}` : "";
  return `${stageHref()}?repo=${queryValue(value.repository)}${ref}`;
}

function badgeUrl(): string {
  return new URL("./brand/git-graph-badge.svg", window.location.href).href;
}

function renderShare(): void {
  const link = target ? shareUrl(target) : stageHref();
  const badge = badgeUrl();
  const snippets = {
    "share-url": link,
    "share-markdown": `[![git graph](${badge})](${link})`,
    "share-html": `<a href="${link}"><img src="${badge}" alt="git graph" height="20" /></a>`
  };
  for (const [id, snippet] of Object.entries(snippets)) {
    const field = document.querySelector<HTMLElement>(`#${id}`)!;
    field.textContent = snippet;
    // initPage's copy handler reads data-copy at click time, so rewriting it
    // here is enough to keep the buttons in step with the current repository.
    field.parentElement?.querySelector<HTMLButtonElement>("[data-copy]")?.setAttribute("data-copy", snippet);
  }
}

function renderTitle(): void {
  document.title = target
    ? `${target.repository} — Web Git Graph`
    : page.locale === "zh"
      ? "提交历史图 — Web Git Graph"
      : "Commit graph — Web Git Graph";
}

function renderStatus(): void {
  statusLine.dataset.state = statusState.kind;
  if (statusState.kind === "empty") status.textContent = page.t("stage.statusEmpty");
  else if (statusState.kind === "connecting")
    status.textContent = page.t("stage.statusConnecting", { repository: statusState.repository });
  else if (statusState.kind === "connected")
    status.textContent = page.t("stage.statusConnected", { repository: statusState.repository });
  else if (statusState.kind === "backend")
    status.textContent = page.t("stage.statusBackend", {
      backend: statusState.backend,
      repository: statusState.repository
    });
  else if (statusState.kind === "rateLimited") status.textContent = page.t("stage.statusRateLimited");
  else status.textContent = page.t("stage.statusError", { message: statusState.message });
}

function token(): string {
  return tokenInput.value.trim();
}

function connect(next: Target): void {
  target = next;
  document.body.dataset.view = "graph";
  repositoryInput.value = next.repository;
  sourceLink.href = `https://github.com/${next.repository}`;
  statusState = { kind: "connecting", repository: next.repository };
  renderStatus();
  renderTitle();
  renderShare();

  const secret = token();
  graph.provider = new GitHubGitGraphProvider({
    repository: next.repository,
    pageSize: 80,
    ...(secret ? { token: secret } : {})
  });
  // `provider` clears any previous ref filter, so the requested refs are applied
  // after it; the second load supersedes the first one it triggered.
  if (next.refs.length) graph.refs = [...next.refs];

  graph.addEventListener(
    "gitgraph-error",
    (event) => {
      const raw = event.detail.error;
      const message = raw instanceof Error ? raw.message : String(raw);
      statusState =
        (raw instanceof GitGraphProtocolError && raw.code === "rate_limited") ||
        /rate limit/i.test(message)
          ? { kind: "rateLimited" }
          : { kind: "error", message };
      renderStatus();
      if (statusState.kind === "rateLimited") tokenInput.focus();
    },
    { once: true }
  );
  window.setTimeout(() => {
    if (statusState.kind === "connecting") {
      statusState = { kind: "connected", repository: next.repository };
      renderStatus();
    }
  }, 900);
}

form.addEventListener("submit", (event) => {
  event.preventDefault();
  const next = parseTarget(repositoryInput.value);
  if (!next) return;
  window.history.pushState(null, "", shareUrl(next));
  connect(next);
});

window.addEventListener("popstate", () => {
  const next = readTargetFromLocation();
  if (next) connect(next);
});

tokenInput.addEventListener("change", () => {
  const secret = token();
  if (secret) sessionStorage.setItem(TOKEN_KEY, secret);
  else sessionStorage.removeItem(TOKEN_KEY);
  // A token pasted after a rate-limited load is only useful if the load is retried.
  if (target && statusState.kind === "rateLimited") connect(target);
});

function setShareOpen(open: boolean): void {
  shareSheet.hidden = !open;
  shareToggle.setAttribute("aria-expanded", String(open));
}

shareToggle.addEventListener("click", () => setShareOpen(shareSheet.hidden));
shareClose.addEventListener("click", () => {
  setShareOpen(false);
  shareToggle.focus();
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !shareSheet.hidden) {
    setShareOpen(false);
    shareToggle.focus();
  }
});
document.addEventListener("pointerdown", (event) => {
  if (shareSheet.hidden) return;
  const node = event.target as Node;
  if (!shareSheet.contains(node) && !shareToggle.contains(node)) setShareOpen(false);
});

document.querySelectorAll<HTMLButtonElement>(".stage-examples button").forEach((button) => {
  button.addEventListener("click", () => {
    const next = parseTarget(button.dataset.repo ?? "");
    if (!next) return;
    window.history.pushState(null, "", shareUrl(next));
    connect(next);
  });
});

const query = new URLSearchParams(window.location.search);
const backendUrl = query.get("backend");
const initial = readTargetFromLocation();
document.body.dataset.view = "empty";
if (backendUrl) {
  // Parity with the landing page: any HTTP v1 backend can drive the stage too.
  const repositoryId = query.get("repository") ?? "local";
  document.body.dataset.view = "graph";
  graph.provider = new HttpGitGraphProvider({ baseUrl: backendUrl, repositoryId });
  statusState = { kind: "backend", backend: backendUrl, repository: repositoryId };
} else if (initial) {
  connect(initial);
} else {
  repositoryInput.focus();
}
renderStatus();
renderShare();
